import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Loader2, CheckCircle2, ExternalLink, Zap } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useOrganizationFeatures } from '../hooks/useOrganizationFeatures';

// ── Interfaces ────────────────────────────────────────────────────────────────

interface CreateLinearIssueModalProps {
    execution: any;
    onClose: () => void;
    /** Optional pre-filled title (e.g. from AutoBugModal). */
    initialSummary?: string;
    /** Optional pre-filled description (e.g. from AutoBugModal). */
    initialDescription?: string;
}

type SubmitState = 'idle' | 'loading' | 'success' | 'error';

// ── Helpers ───────────────────────────────────────────────────────────────────

const getApiUrl = (): string =>
    window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
        ? import.meta.env.VITE_API_URL
        : 'http://localhost:3000';

const INPUT_CLASS =
    'w-full px-3 py-2 text-sm border border-slate-200 dark:border-gh-border-dark rounded-lg ' +
    'bg-white dark:bg-gh-bg-dark text-slate-900 dark:text-slate-100 ' +
    'placeholder-slate-400 dark:placeholder-slate-600 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition';

// ── Component ─────────────────────────────────────────────────────────────────

export function CreateLinearIssueModal({
    execution,
    onClose,
    initialSummary,
    initialDescription,
}: CreateLinearIssueModalProps) {
    const { token } = useAuth();
    const { integrations } = useOrganizationFeatures();

    const [title, setTitle] = useState(
        initialSummary ?? (execution?.taskId ? `Bug in execution ${execution.taskId}` : 'Bug report'),
    );
    const [description, setDescription] = useState(initialDescription ?? '');
    const [submitState, setSubmitState] = useState<SubmitState>('idle');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [createdIssue, setCreatedIssue] = useState<{ id: string; url: string; identifier: string } | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim()) return;

        setSubmitState('loading');
        setErrorMessage(null);

        try {
            const API_URL = getApiUrl();
            const res = await axios.post(
                `${API_URL}/api/linear/issues`,
                {
                    executionId: execution?.taskId ?? execution?._id,
                    title: title.trim(),
                    description: description.trim() || undefined,
                },
                { headers: { Authorization: `Bearer ${token}` } },
            );

            if (res.data.success) {
                setCreatedIssue(res.data.data);
                setSubmitState('success');
            } else {
                throw new Error(res.data.error ?? 'Failed to create issue');
            }
        } catch (err: any) {
            setErrorMessage(err?.response?.data?.error ?? err?.message ?? 'Failed to create Linear issue');
            setSubmitState('error');
        }
    };

    const teamId = integrations.linear?.teamId;

    const modal = (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            role="button"
            tabIndex={-1}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
        >
            <div
                className="relative w-full max-w-lg bg-white dark:bg-gh-bg-subtle-dark rounded-2xl shadow-2xl border border-slate-100 dark:border-gh-border-dark flex flex-col max-h-[90vh]"
                role="dialog"
                aria-modal="true"
                aria-labelledby="linear-modal-title"
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-gh-border-dark flex-shrink-0">
                    <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-md bg-indigo-600 flex items-center justify-center">
                            <Zap size={14} className="text-white" />
                        </div>
                        <h2 id="linear-modal-title" className="text-sm font-semibold text-slate-800 dark:text-gh-text-dark">
                            Create Linear Issue
                        </h2>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        aria-label="Close"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    {submitState === 'success' && createdIssue ? (
                        <div className="flex flex-col items-center gap-4 py-6 text-center">
                            <CheckCircle2 size={40} className="text-emerald-500" />
                            <div>
                                <p className="text-sm font-semibold text-slate-800 dark:text-gh-text-dark">
                                    Issue created: <span className="text-indigo-600 dark:text-indigo-400">{createdIssue.identifier}</span>
                                </p>
                                <a
                                    href={createdIssue.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1 mt-2 text-sm text-indigo-500 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
                                >
                                    View in Linear <ExternalLink size={12} />
                                </a>
                            </div>
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
                            >
                                Done
                            </button>
                        </div>
                    ) : (
                        <form id="linear-issue-form" onSubmit={handleSubmit} className="space-y-4">
                            {teamId && (
                                <p className="text-xs text-slate-400 dark:text-slate-500">
                                    Team ID: <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">{teamId}</code>
                                </p>
                            )}

                            <div>
                                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5" htmlFor="linear-title">
                                    Title
                                </label>
                                <input
                                    id="linear-title"
                                    type="text"
                                    required
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    className={INPUT_CLASS}
                                    placeholder="Brief description of the issue"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5" htmlFor="linear-description">
                                    Description <span className="font-normal text-slate-400">(optional)</span>
                                </label>
                                <textarea
                                    id="linear-description"
                                    rows={6}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className={INPUT_CLASS + ' resize-y'}
                                    placeholder="Detailed description, steps to reproduce, etc."
                                />
                            </div>

                            {submitState === 'error' && errorMessage && (
                                <div className="flex items-start gap-2 rounded-lg px-3 py-2.5 text-sm bg-rose-50 dark:bg-rose-950/30 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-400">
                                    <span>{errorMessage}</span>
                                </div>
                            )}
                        </form>
                    )}
                </div>

                {/* Footer */}
                {submitState !== 'success' && (
                    <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 dark:border-gh-border-dark bg-slate-50 dark:bg-gh-bg-dark rounded-b-2xl flex-shrink-0">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-semibold text-slate-600 dark:text-slate-300 bg-white dark:bg-gh-bg-dark border border-slate-200 dark:border-gh-border-dark rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="linear-issue-form"
                            disabled={submitState === 'loading' || !title.trim()}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                        >
                            {submitState === 'loading' && <Loader2 size={14} className="animate-spin" />}
                            {submitState === 'loading' ? 'Creating…' : 'Create Issue'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );

    return createPortal(modal, document.body);
}
